import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { CalendarBot } from '../services/calendarWebSocketService';
import { fetchWatchableSessions } from '../services/watchableSessions';
import { WATCH_SESSION_COPY } from '../constants/watchSession';
import { formatStartTime } from '../utils/formatStartTime';
import type { WatchableSession } from '../types/liveSession';
import { Button } from './ui/button';
import LiveSessionRow from './LiveSessionRow';
import JoinChoiceStep from './JoinChoiceStep';
import Spinner from './ui/Spinner';

/**
 * "Watch a Session" — the same button+modal pattern as the desktop apps. Lists:
 *  - YOUR MEETINGS: this user's calendar/manual bots (connect to a live one)
 *  - LIVE SESSIONS: every live call in the tenant (owner decision 2026-10-03),
 *    bot and desktop alike, with its owner, mode and start time. The user's
 *    own bot resumes as its owner. Anyone else's call offers one Join, which
 *    opens the choice step over the list (owner, 2026-10-07): a desktop call
 *    can be joined as a co-seller WITH THE USER'S MIC (D6) or as an observer,
 *    a bot call only as an observer. A co-seller join shows its joining state
 *    and any failure in the step; an observer join is what Watch was: the
 *    list's connecting state, and a failure under the list.
 */

interface WatchSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  calendarBots: CalendarBot[];
  /** The call being joined (a bot id or a desktop session id), while a join is in flight. */
  connectingSessionId: string | null;
  /** Why the last desktop join failed, shown under the list. */
  joinError?: string | null;
  /** `isOwner`: the signed-in user owns the bot (false joins it as a watcher). */
  onConnect: (
    botId: string,
    websocketUrl?: string,
    botSessionId?: string,
    isSalespersonHost?: boolean,
    isOwner?: boolean
  ) => void;
  /** Join someone's desktop call as an observer (watch it). */
  onWatchStandalone: (session: WatchableSession) => void;
  /** Join someone's desktop call as a co-seller with the user's own mic (the picked microphone). */
  onJoinWithMic: (session: WatchableSession, deviceId: string | null) => void;
  /** The join in flight is a co-seller join: the choice step shows its joining state. */
  isJoiningWithMic?: boolean;
}

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-success-tint text-success' },
  creating: { label: 'Creating...', className: 'bg-warning-tint text-warning' },
  joining: { label: 'In Lobby', className: 'bg-warning-tint text-warning' },
  scheduled: { label: 'Scheduled', className: 'bg-info-tint text-info' },
};

const ConnectingState: React.FC = () => (
  <div className="flex items-center space-x-2 py-3">
    <Spinner />
    <span className="text-[13px] text-ink-secondary">{WATCH_SESSION_COPY.connecting}</span>
  </div>
);


const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[10px] font-bold text-ink-muted mt-2.5 mb-0.5">{children}</p>
);

const CalendarBotRow: React.FC<{ bot: CalendarBot; onConnect: WatchSessionModalProps['onConnect'] }> = ({
  bot,
  onConnect,
}) => {
  const status = STATUS_LABELS[bot.status] || { label: bot.status, className: 'bg-surface-subtle text-ink-secondary' };
  const canConnect = !!bot.botId && (bot.status === 'active' || bot.status === 'joining');
  return (
    <div className="flex items-center justify-between border-b border-hairline py-2.5">
      <div className="min-w-0 pr-3">
        <p className="text-[13px] font-semibold text-ink truncate">{bot.eventTitle}</p>
        <p className="text-[11px] text-ink-muted mt-0.5">
          {formatStartTime(bot.startTime)}
          <span className={`ml-2 px-1.5 py-0.5 rounded-full ${status.className}`}>{status.label}</span>
        </p>
      </div>
      {canConnect ? (
        <Button
          variant="secondary"
          size="sm"
          // The calendar socket lists only this user's bots: the owner.
          onClick={() => onConnect(bot.botId, bot.websocketUrl, bot.botSessionId, bot.isSalespersonHost, true)}
          className="shrink-0 text-xs"
        >
          {WATCH_SESSION_COPY.connect}
        </Button>
      ) : (
        <span className="shrink-0 text-[11px] text-ink-muted">
          {bot.status === 'scheduled' ? WATCH_SESSION_COPY.joinsAutomatically : WATCH_SESSION_COPY.notReady}
        </span>
      )}
    </div>
  );
};

const WatchSessionModal: React.FC<WatchSessionModalProps> = ({
  isOpen,
  onClose,
  calendarBots,
  connectingSessionId,
  joinError,
  onConnect,
  onWatchStandalone,
  onJoinWithMic,
  isJoiningWithMic = false,
}) => {
  const [liveSessions, setLiveSessions] = useState<WatchableSession[]>([]);
  // The call whose choice step (Join as Co-Seller / Join as Observer) is open.
  const [choiceSession, setChoiceSession] = useState<WatchableSession | null>(null);
  // The call last joined as a co-seller: `joinError` is that join's, shown in its step, not under the list.
  const [micJoinSession, setMicJoinSession] = useState<WatchableSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [listErrors, setListErrors] = useState<string[]>([]);

  const isConnecting = !!connectingSessionId;
  const visibleBots = calendarBots.filter((bot) => bot.status !== 'terminated' && bot.status !== 'failed');

  const loadLiveSessions = useCallback(async () => {
    setIsLoading(true);
    setListErrors([]);
    try {
      const { sessions, errors } = await fetchWatchableSessions();
      setLiveSessions(sessions);
      setListErrors(errors);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadLiveSessions();
      return;
    }
    setChoiceSession(null);
    setMicJoinSession(null);
  }, [isOpen, loadLiveSessions]);

  const handleJoinAsCoSeller = useCallback(
    (deviceId: string | null) => {
      if (!choiceSession) return;
      setMicJoinSession(choiceSession);
      onJoinWithMic(choiceSession, deviceId);
    },
    [choiceSession, onJoinWithMic]
  );

  const handleSelect = useCallback(
    (session: WatchableSession) => {
      setMicJoinSession(null);
      if (session.mode === 'standalone') {
        onWatchStandalone(session);
        return;
      }
      // Your own bot resumes as its owner; anyone else's is watched (isOwner false).
      onConnect(session.sessionId, session.websocketUrl || undefined, undefined, undefined, session.isOwn);
    },
    [onConnect, onWatchStandalone]
  );

  // Join as Observer runs exactly what Watch did, from the list: its connecting state, its errors.
  const handleJoinAsObserver = useCallback(() => {
    if (!choiceSession) return;
    setChoiceSession(null);
    handleSelect(choiceSession);
  }, [choiceSession, handleSelect]);

  if (!isOpen) return null;

  const isEmpty = !isLoading && visibleBots.length === 0 && liveSessions.length === 0 && listErrors.length === 0;

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center">
      {/* Backdrop — click closes unless a join is in flight */}
      <div
        className="absolute inset-0 bg-[rgba(28,27,24,0.5)]"
        onClick={() => {
          if (!isConnecting) onClose();
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={WATCH_SESSION_COPY.title}
        className="relative w-[460px] max-w-[92vw] modal-panel overflow-hidden"
      >
        {/* Header */}
        <div className="bg-signature px-5 py-3.5">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-on-signature text-[17px] font-bold">{WATCH_SESSION_COPY.title}</h2>
              <p className="text-signature-tint text-xs mt-0.5">{WATCH_SESSION_COPY.subtitle}</p>
            </div>
            <Button
              variant="tertiary"
              onClick={onClose}
              disabled={isConnecting}
              className="text-on-signature hover:bg-white/10 disabled:opacity-40"
              title={WATCH_SESSION_COPY.close}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 pt-2 pb-1">
          {choiceSession ? (
            <JoinChoiceStep
              session={choiceSession}
              isJoining={isConnecting && isJoiningWithMic}
              joinError={micJoinSession?.sessionId === choiceSession.sessionId ? joinError ?? null : null}
              onJoinAsCoSeller={handleJoinAsCoSeller}
              onJoinAsObserver={handleJoinAsObserver}
              onCancel={() => setChoiceSession(null)}
            />
          ) : isConnecting ? (
            /* Connecting state: replaces the lists so the modal never looks dead */
            <ConnectingState />
          ) : (
            <>
              <div className="max-h-[320px] overflow-y-auto">
                {visibleBots.length > 0 && (
                  <>
                    <SectionLabel>{WATCH_SESSION_COPY.yourMeetings}</SectionLabel>
                    {visibleBots.map((bot) => (
                      <CalendarBotRow key={bot.botSessionId} bot={bot} onConnect={onConnect} />
                    ))}
                  </>
                )}

                <SectionLabel>{WATCH_SESSION_COPY.liveSessions}</SectionLabel>
                {isLoading && <p className="text-[13px] text-ink-muted py-3">{WATCH_SESSION_COPY.loading}</p>}
                {isEmpty && <p className="text-[13px] text-ink-muted py-3">{WATCH_SESSION_COPY.empty}</p>}
                {!isLoading && liveSessions.length > 0 && (
                  <ul aria-label={WATCH_SESSION_COPY.liveSessions}>
                    {liveSessions.map((session) => (
                      <LiveSessionRow
                        key={`${session.mode}-${session.sessionId}`}
                        session={session}
                        onResume={handleSelect}
                        onJoin={setChoiceSession}
                      />
                    ))}
                  </ul>
                )}
              </div>

              {[...listErrors, ...(joinError && !micJoinSession ? [joinError] : [])].map((message) => (
                <p key={message} role="alert" className="text-xs text-error py-1">
                  {message}
                </p>
              ))}
            </>
          )}
        </div>

        {/* Footer: the choice step has its own Cancel, so it shows none */}
        {!choiceSession && (
          <div className="bg-surface-subtle px-5 py-3 flex justify-end space-x-2">
            <Button
              variant="tertiary"
              size="sm"
              onClick={loadLiveSessions}
              disabled={isConnecting || isLoading}
              className="text-xs"
            >
              {WATCH_SESSION_COPY.refresh}
            </Button>
            <Button variant="tertiary" size="sm" onClick={onClose} disabled={isConnecting} className="text-xs">
              {WATCH_SESSION_COPY.cancel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WatchSessionModal;
